import React, { useEffect, useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationTipoCirurgiaSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import tipoCirurgiaService from "../../../services/tipoCirurgiaService.ts";
import { TipoCirurgia } from "../../../models/TipoCirurgia.ts";

interface UpdateTipoCirurgiaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (r: any) => void;
  tipoCirurgia: any;
  readOnly?: boolean;
}

// Mesma receita de cores dos demais módulos — sem ThemeProvider do MUI,
// estados de label/borda/desabilitado caem no tema claro padrão do MUI, sem
// contraste no fundo escuro. CSS vars do design system em vez de tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiInputLabel-root.Mui-disabled": { color: "var(--ink-faint)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
    "&.Mui-disabled fieldset": { borderColor: "var(--border)" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "var(--ink-faint)",
    color: "var(--ink-faint)",
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const UpdateTipoCirurgiaDrawer: React.FC<UpdateTipoCirurgiaDrawerProps> = ({
  isOpen,
  onClose,
  onUpdate,
  tipoCirurgia,
  readOnly = false,
}) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationTipoCirurgiaSchema),
  });

  useEffect(() => {
    if (tipoCirurgia) {
      reset({
        nome: tipoCirurgia.nome,
        observacao: tipoCirurgia.observacao || "",
      });
    }
  }, [tipoCirurgia, reset]);

  const onSubmit = async (data: any) => {
    if (readOnly) return onClose();

    const updated = new TipoCirurgia(tipoCirurgia.id, data.nome, data.observacao);

    try {
      setIsLoading(true);
      const resp = await tipoCirurgiaService.update(updated);
      showFeedback(resp.message, "success");
      onUpdate?.(resp);
      onClose();
    } catch (err: any) {
      showFeedback(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{readOnly ? "Visualizar Tipo de Cirurgia" : "Alterar Tipo de Cirurgia"}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="update-tipocirurgia-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do Tipo de Cirurgia*" size="small" fullWidth disabled={readOnly} sx={fieldSx}
              {...register("nome")} autoComplete="off" error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observação" size="small" fullWidth multiline minRows={3} disabled={readOnly} sx={fieldSx}
              {...register("observacao")} error={!!errors.observacao} helperText={errors.observacao?.message as string}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={onClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          {readOnly ? "Fechar" : "Cancelar"}
        </MuiButton>
        {!readOnly && (
          <MuiButton type="submit" form="update-tipocirurgia-form" variant="contained" disabled={isLoading} disableElevation>
            {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Salvar Alterações"}
          </MuiButton>
        )}
      </Box>
    </Drawer>
  );
};

export default UpdateTipoCirurgiaDrawer;
